import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Search, Help, VpnKey, Payment, Notifications, Error as ErrorIcon } from '@mui/icons-material';
import FAQSection from '../components/faq/FAQSection';
import { faqCategories, faqData } from '../data/faqData';

const iconMap = {
    VpnKey,
    Payment,
    Notifications,
    Error: ErrorIcon,
    Help
};

const FAQ = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');

    const filteredData = useMemo(() => {
        let filtered = {};

        Object.keys(faqData).forEach(categoryId => {
            if (selectedCategory !== 'all' && selectedCategory !== categoryId) {
                return;
            }

            const questions = faqData[categoryId].filter(item => {
                if (!searchTerm) return true;
                const searchLower = searchTerm.toLowerCase();
                return (
                    item.question.toLowerCase().includes(searchLower) ||
                    item.answer.toLowerCase().includes(searchLower)
                );
            });

            if (questions.length > 0) {
                filtered[categoryId] = questions;
            }
        });

        return filtered;
    }, [searchTerm, selectedCategory]);

    const totalResults = Object.values(filteredData).reduce((sum, questions) => sum + questions.length, 0);

    return (
        <>
            <Helmet>
                <title>FAQ - Questions Fréquentes - Netikash Developer Portal</title>
                <meta name="description" content="FAQ Netikash : réponses aux questions fréquentes sur l'API, l'authentification, les paiements, les webhooks et plus encore." />
                <meta property="og:title" content="FAQ - Netikash Developer Portal" />
                <link rel="canonical" href="https://dev.netikash.com/faq" />
            </Helmet>
            <div className="min-h-screen bg-gray-50 dark:bg-slate-950 dark:text-slate-100 pt-16">
                {/* Hero Section */}
                <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center">
                            <div className="inline-flex items-center px-6 py-3 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-6">
                                <Help className="mr-2" />
                                <span className="font-semibold text-lg">QUESTIONS FRÉQUENTES</span>
                            </div>
                            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
                                FAQ
                            </h1>
                            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
                                Trouvez rapidement des réponses à vos questions sur l'API Netikash
                            </p>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    {/* Search and Filters */}
                    <div className="mb-8">
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
                            <div className="flex items-center space-x-4 mb-4">
                                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                    <Search className="text-amber-700" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-gray-900 mb-1">Rechercher dans la FAQ</h3>
                                    <p className="text-sm text-gray-600">Tapez un mot-clé ou une question</p>
                                </div>
                            </div>
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Ex: authentification, paiement, webhook, erreur..."
                                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            />
                            {searchTerm && (
                                <div className="mt-3 text-sm text-gray-600">
                                    {totalResults} résultat{totalResults !== 1 ? 's' : ''} trouvé{totalResults !== 1 ? 's' : ''}
                                </div>
                            )}
                        </div>

                        {/* Category Filters */}
                        <div className="flex flex-wrap gap-3">
                            <button
                                onClick={() => setSelectedCategory('all')}
                                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                                    selectedCategory === 'all'
                                        ? 'bg-amber-500 text-white'
                                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                                Tous
                            </button>
                            {faqCategories.map((category) => {
                                const Icon = iconMap[category.icon] || Help;
                                return (
                                    <button
                                        key={category.id}
                                        onClick={() => setSelectedCategory(category.id)}
                                        className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center space-x-2 ${
                                            selectedCategory === category.id
                                                ? 'bg-amber-500 text-white'
                                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        <Icon fontSize="small" />
                                        <span>{category.title}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* FAQ Content */}
                    {Object.keys(filteredData).length === 0 ? (
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
                            <Help className="text-gray-400 text-6xl mx-auto mb-4" />
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucun résultat trouvé</h3>
                            <p className="text-gray-600 mb-6">
                                Essayez de modifier vos critères de recherche ou de consulter une autre catégorie.
                            </p>
                            <button
                                onClick={() => {
                                    setSearchTerm('');
                                    setSelectedCategory('all');
                                }}
                                className="px-6 py-2 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 transition-all"
                            >
                                Réinitialiser
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-12">
                            {Object.keys(filteredData).map((categoryId) => {
                                const category = faqCategories.find(cat => cat.id === categoryId);
                                const Icon = category ? iconMap[category.icon] || Help : Help;
                                return (
                                    <div key={categoryId}>
                                        <div className="flex items-center space-x-3 mb-6">
                                            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                                                <Icon className="text-amber-600" />
                                            </div>
                                            <div>
                                                <h2 className="text-2xl font-bold text-gray-900">
                                                    {category ? category.title : categoryId}
                                                </h2>
                                                {category && (
                                                    <p className="text-gray-600 text-sm">{category.description}</p>
                                                )}
                                            </div>
                                        </div>
                                        <FAQSection
                                            category={categoryId}
                                            questions={filteredData[categoryId]}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Help Section */}
                    <div className="mt-12 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl shadow-sm border border-amber-200 p-8">
                        <div className="text-center">
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                Vous ne trouvez pas votre réponse ?
                            </h3>
                            <p className="text-gray-700 mb-6">
                                Notre équipe de support est là pour vous aider. Contactez-nous ou consultez notre centre d'aide.
                            </p>
                            <div className="flex flex-wrap gap-4 justify-center">
                                <a
                                    href="/contact"
                                    className="px-6 py-3 bg-gradient-to-r from-amber-500 to-yellow-600 text-white rounded-lg font-medium hover:from-amber-600 hover:to-yellow-700 transition-all"
                                >
                                    Nous contacter
                                </a>
                                <a
                                    href="/help-center"
                                    className="px-6 py-3 bg-white border border-amber-300 text-amber-700 rounded-lg font-medium hover:bg-amber-50 transition-all"
                                >
                                    Centre d'aide
                                </a>
                                <a
                                    href="/technical-support"
                                    className="px-6 py-3 bg-white border border-amber-300 text-amber-700 rounded-lg font-medium hover:bg-amber-50 transition-all"
                                >
                                    Support technique
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default FAQ;